import {
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { getUnixTime, parseISO } from "date-fns";
import {
  ApiConversation,
  ChatMessage,
  FormattedChatMessage,
  FormattedConversation,
  WolUser,
} from "../../app/types";
import api from "../api/api";
import { SocketChatMessage } from "../sockets/types";

const chatEntities = createEntityAdapter<FormattedConversation>({
  selectId: (conversation) => conversation.topic,
});

const { selectById } = chatEntities.getSelectors();

function formatMessage(
  message: ChatMessage,
  avatar: string,
  name: string,
): FormattedChatMessage {
  return {
    _id: message.id,
    text: message.message,
    createdAt: getUnixTime(parseISO(message.createDate)),
    user: {
      _id: message.author,
      avatar,
      name,
    },
    image: message.imageUrl !== null ? message.imageUrl : undefined,
    isQuestion: message.isQuestion,
    isAnswered: message.isAnswered,
  };
}

function formatConversation(
  conversation: ApiConversation,
): FormattedConversation {
  const participants = conversation.participants.reduce(
    (previous, participant) => ({
      ...previous,
      [participant.profileId]: participant,
    }),
    {} as Record<string, WolUser>,
  );

  const formattedMessages = conversation.messages.reduce(
    (previous, message) => ({
      ...previous,
      [message.id]: formatMessage(
        message,
        participants[message.author]?.avatarUrl ?? "",
        `${participants[message.author]?.firstName ?? ""} ${
          participants[message.author]?.lastName ?? ""
        }`,
      ),
    }),
    {} as Record<string, FormattedChatMessage>,
  );

  return {
    ...conversation,
    formattedParticipants: participants,
    formattedMessages,
  };
}

const chatSlice = createSlice({
  name: "conversations",
  initialState: chatEntities.getInitialState,
  reducers: {
    receiveChatMessage: {
      prepare: (message: SocketChatMessage) => ({
        payload: message,
      }),
      reducer: (state, action: PayloadAction<SocketChatMessage>) => {
        const conversation = selectById(state, action.payload.chatTopic);
        if (!conversation) {
          return state;
        }

        const message: ChatMessage = {
          id: +action.payload.PostFeedId,
          message: action.payload.PosterMessageBasic.trim(),
          author: +action.payload.PostProfileId,
          createDate: action.payload.PostTimeISO8601,
          topic: action.payload.chatTopic,
          imageUrl: null,
          isUnread: true,
          isQuestion: action.payload.isQuestion,
          isAnswered: false,
        };
        const messages = [...conversation.messages, message];

        const formattedMessage = formatMessage(
          message,
          action.payload.PosterAvatarURL,
          `${action.payload.PosterFirstName} ${action.payload.PosterLastName}`,
        );
        const formattedMessages = {
          ...conversation.formattedMessages,
          [message.id]: formattedMessage,
        };

        return chatEntities.updateOne(state, {
          id: action.payload.chatTopic,
          changes: { messages, formattedMessages },
        });
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.getConversation.matchFulfilled,
      (state, action) =>
        chatEntities.upsertOne(state, formatConversation(action.payload)),
    );
    builder.addMatcher(
      api.endpoints.postChatMessage.matchFulfilled,
      (state, action) => {
        const conversation = selectById(state, action.payload.topic);
        if (!conversation) {
          return state;
        }
        const participants = conversation.formattedParticipants;

        const messages = [...conversation.messages, action.payload];

        const formattedMessages = {
          ...conversation.formattedMessages,
          [action.payload.id]: formatMessage(
            action.payload,
            participants[action.payload.author]?.avatarUrl ?? "",
            `${participants[action.payload.author]?.firstName ?? ""} ${
              participants[action.payload.author]?.lastName ?? ""
            }`,
          ),
        };
        return chatEntities.updateOne(state, {
          id: action.payload.topic,
          changes: { formattedMessages, messages },
        });
      },
    );
  },
});

export default chatSlice;
export const { receiveChatMessage } = chatSlice.actions;
